export interface IdleOptions {
  /** How long the work may go without settling or moving before it is abandoned. */
  idleFor: number;

  /** A number that rises while the work is getting somewhere, such as bytes sent. */
  progress: () => number;

  /** What the rejection says. */
  message: string;

  /** Called once, immediately before giving up, so the caller can stop the work. */
  onGiveUp?: () => void;
}

/**
 * Gives a promise an end, without cutting off work that is still moving.
 *
 * Rejects with `message` once `work` has gone `idleFor` milliseconds without
 * settling and without `progress()` changing. Anything still making headway
 * keeps resetting the clock, so the total time is unbounded and only silence is
 * fatal. A fixed deadline cannot tell the two apart, and would abandon a large
 * photo going up over a slow connection just as readily as a dead one.
 *
 * A promise that never settles is not hypothetical here: ember-file-upload
 * finishes an upload by building a Response from the completed XHR, and one
 * that failed at the network layer reports status 0, which the Response
 * constructor rejects as out of range. The throw lands inside the XHR's own
 * error handler before either resolve or reject runs, so the upload promise is
 * left pending for good and the file never leaves the queue.
 *
 * That failure reports itself immediately and then goes quiet, which is exactly
 * what this notices.
 */
export function giveUpWhenIdle<T>(work: Promise<T>, options: IdleOptions): Promise<T> {
  const { idleFor, progress, message, onGiveUp } = options;

  let timer = 0;

  const abandon = new Promise<never>((_, reject) => {
    let seen = progress();

    // Checked one window at a time rather than against a clock: if anything
    // moved since the last look, the work is alive and the window starts again.
    // Worst case this waits two windows before giving up, which is a small
    // price when the alternative is waiting for ever.
    const check = () => {
      const moved = progress();

      if (moved !== seen) {
        seen = moved;
        timer = setTimeout(check, idleFor);

        return;
      }

      onGiveUp?.();
      reject(new Error(message));
    };

    timer = setTimeout(check, idleFor);
  });

  // Cleared whichever way the race goes, so work that finished in time does not
  // hold a timer until it fires.
  return Promise.race([work, abandon]).finally(() => clearTimeout(timer));
}
